import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useHub } from "../lib/dados";
import { registrarAbertura } from "../lib/uso";
import { marcarNovidadeVista, novidadeJaVista, useNovidades, type Novidade } from "../lib/novidades";
import Carregando from "../componentes/Carregando";
import Rodape from "../componentes/Rodape";
import { Aviso, Topo } from "../componentes/ui";
import { BarraDeVolta, cursoDeVolta, type Volta } from "../componentes/FerramentaUi";
import BotaoAvaliarFerramenta from "../componentes/AvaliarFerramenta";
import IrProposta from "./IrProposta";
import FerramentaDeConteudo from "./FerramentaDeConteudo";
import MeiPro from "./MeiPro";
import IaDoCurso from "./IaDoCurso";
import SimuladorMei from "./SimuladorMei";
import GeradorCurriculos from "./GeradorCurriculos";
import CalendarioTributario from "./CalendarioTributario";
import HubDp from "./HubDp";
import LegalizaFacil from "./LegalizaFacil";
import GerenciadorDeEstudos from "./GerenciadorEstudos";
import MapaDeConcursos from "./MapaConcursos";
import ForoFacil from "./ForoFacil";
import LeitorDeProcessos from "./LeitorDeProcessos";
import CursoBonus from "./CursoBonus";
import PlanoEstudos from "./PlanoEstudos";
import RadarSimples from "./RadarSimples";
import SindiFacil from "./SindiFacil";
import CnaExpert from "./CnaExpert";
import Calculadoras from "./Calculadoras";
import CstClassTrib from "./CstClassTrib";
import CentralIbsCbs from "./CentralIbsCbs";
import CursoNoHub from "./CursoNoHub";
import CentralDoPerito from "./CentralDoPerito";

// A porta das ferramentas que moram no hub — `cards.destino = 'interno'` (000065).
//
// ---------------------------------------------------------------------------
// Por que uma rota por SLUG, e não uma rota por ferramenta
// ---------------------------------------------------------------------------
// A rota é derivada do slug do card (`/ferramenta/ir-proposta`), e a derivação está
// em `aberturaDoCard` — um lugar só. Quando MEI PRO e IR PRO forem construídos (as
// outras duas que Victor classificou como "construir aqui dentro"), a migration liga
// `destino = 'interno'`, aqui entra uma linha no despacho, e nada mais muda: nem o
// card, nem a vitrine, nem `abrir_card`.
//
// A alternativa — uma rota nomeada por ferramenta e a url guardada em `cards.url` —
// criaria um estado a mais para dar errado (card interno com url de outra coisa) e
// enfraqueceria a invariante do teste 36, que exige `https` e domínio da casa em
// toda `cards.url` justamente porque ela vira `href`.
//
// ---------------------------------------------------------------------------
// Esta tela NÃO decide acesso — ela LÊ a decisão
// ---------------------------------------------------------------------------
// A checagem abaixo existe para o aluno que digitou a rota à mão ou guardou um link
// de quando tinha o curso. Ela não é a trava: a trava é a RLS das tabelas da
// ferramenta, que devolve zero linha para quem não é dono, e a `vw_minha_vitrine`,
// que não entrega o card a quem não tem o produto. Se esta função fosse a única
// defesa, bastaria desligar o JavaScript.
//
// Curso trancado por contrato chega aqui como `destino = 'em-breve'` — a view anula
// antes (000044 a 000046), então a mensagem certa sai sem esta tela precisar
// conhecer a regra do contrato.

/** Slug do card → a tela dele. Ferramenta nova entra aqui e em mais nenhum lugar. */
const FERRAMENTAS: Record<string, () => React.ReactElement> = {
  "ir-proposta": IrProposta,
  // Ferramenta de CONTEÚDO: a tela é genérica e o card é o parâmetro.
  "ir-pro": () => <FerramentaDeConteudo card="ir-pro" />,
  // O MEI PRO tem DUAS metades — conteúdo (9 seções, 43 páginas, 000076) e a
  // gestão de clientes com livro-caixa (000077) —, então o dele é um wrapper
  // que despacha pelo primeiro segmento. A tela genérica continua genérica.
  "mei-pro": MeiPro,
  // A IA de curso: uma tela para as duas (Contabiliza.AI, e depois Peric.IA). O
  // curso — que decide o corpus — vem na query `?curso=`, não no slug, porque o
  // mesmo card serve dois cursos. A tela resolve o curso sozinha quando a query
  // falta, então o card basta aqui.
  "contabiliza-ai": () => <IaDoCurso card="contabiliza-ai" nome="Contabiliza.AI" />,
  "peric-ia": () => <IaDoCurso card="peric-ia" nome="Peric.IA" />,
  // O Simulador MEI tem TRÊS rotinas (abertura, das, dasn), despachadas pelo
  // primeiro segmento — o mesmo desenho do MEI PRO, e pela mesma razão: são
  // fluxos independentes de um card só, não três cards.
  "simulador-mei": SimuladorMei,
  // O Gerador de Currículos: o painel dos currículos do aluno e o editor. O
  // segmento depois do slug é o ID do currículo (um uuid), não uma palavra
  // reservada — por isso aqui não há lista de segmentos como no MEI PRO.
  "gerador-de-curriculos": GeradorCurriculos,
  // O Calendário Tributário Federal: uma tela com duas abas (o calendário e os
  // clientes do contador), não duas rotas — o cliente aqui é um RÓTULO de filtro
  // do próprio calendário, e não um assunto com endereço próprio como o livro do
  // MEI. Por isso não há despacho por segmento.
  "calendario-tributario": CalendarioTributario,
  // O HUB DP tem CINCO módulos (holerite, ferias-13, encargos, esocial,
  // admissao), despachados pelo primeiro segmento — e o SEGUNDO segmento é a aba
  // do módulo, o que faz dele o único com dois níveis de sub-rota. As abas são
  // rota, e não estado, para o aluno poder voltar pelo navegador e compartilhar o
  // link de uma aba.
  "hub-dp": HubDp,
  // O Legaliza Fácil: o primeiro segmento é a SIGLA do estado (`/SP`), e depois
  // dela `modelo/<id>` abre o documento para preencher. Não há lista de segmentos
  // reservados como no MEI PRO porque o segmento é um dado — as 27 siglas —, e a
  // única palavra reservada é `modelo`.
  "legaliza-facil": LegalizaFacil,
  // O Gerenciador de Estudos (000117): o cronograma de 275 metas do Aprovação
  // Contábil. O segundo segmento é `meta/<numero>` — e o número é a IDENTIDADE
  // da meta no Bubble (denso 1..275, sem buraco), então ele serve de rota sem
  // tradução nenhuma.
  //
  // CUIDADO com o nome: no Bubble "Gerenciador de Estudos" também designa o
  // Plano de Estudos da ESCOLA, que Victor decidiu NÃO migrar. Este é o do
  // Aprovação, domínio `ac-*`.
  "gerenciador-de-estudos": GerenciadorDeEstudos,
  // O Mapa de Concursos Contábeis (000120): UMA tela, sem sub-rota. O estado e a
  // situação da inscrição viajam na QUERY (`?uf=PR&situacao=aberta`), e não no
  // caminho, porque são FILTROS de uma lista só — o aluno manda "os abertos no
  // Paraná" para o colega e o botão de voltar desfaz o filtro em vez de sair da
  // ferramenta. Sub-rota é para seção com endereço próprio, como as cinco do
  // HUB DP.
  "mapa-de-concursos": MapaDeConcursos,
  // O ForoFácil (000169/000170): o cadastro como perito nos 27 TJs e 6 TRFs. O
  // único segmento é a SIGLA do tribunal (`/SP`, `/TRF4`), como no Legaliza; o
  // alternador TJ/TRF da entrada viaja na query (`?modo=trf`), porque é um filtro
  // do mesmo mapa, não uma seção com endereço próprio.
  "foro-facil": ForoFacil,
  // O Leitor Inteligente de Processos (000171): a análise de um processo judicial
  // pela IA. Dois tipos de segmento depois do slug — a palavra reservada `nova`
  // (o envio dos PDFs) e o UUID de uma análise, que abre o resultado. Dentro da
  // análise vem um terceiro segmento, a ABA (`/<uuid>/checklist`), que é sub-rota
  // pela razão do HUB DP: o perito manda o link de uma aba ao colega e recarregar
  // não o joga de volta ao relatório.
  //
  // CUIDADO com o nome: este NÃO é a Peric.IA. `peric-ia` é o outro card do mesmo
  // curso, a IA de curso que responde sobre o material das aulas, e Victor
  // decidiu em 02/09 não mexer nela. O assistente daqui responde sobre o processo
  // que o aluno acabou de enviar.
  "leitor-de-processos": LeitorDeProcessos,
  // Os três cursos BÔNUS da Pós Finanças (000177). Uma tela só, com o card como
  // parâmetro — como `FerramentaDeConteudo` faz para o IR Pro e o MEI Pro —,
  // porque as três mostram a mesma coisa com dados diferentes. Sem sub-rota: a
  // aula não abre aqui dentro, ela abre no Memberkit, e o caminho dela é a ponte
  // `/abrir/<curso>/<card>/<aula>`.
  "excel-financas": () => <CursoBonus card="excel-financas" />,
  "sheets-financas": () => <CursoBonus card="sheets-financas" />,
  "ia-financas": () => <CursoBonus card="ia-financas" />,
  // O Plano de Estudos Inteligente (000382): o cronograma do Exame CFC, nas MESMAS tabelas dos cursos
  // bônus (as aulas do CFC de A a Z no Memberkit), com a regra do tempo de hoje por cima.
  "plano-estudos-inteligente": () => <PlanoEstudos card="plano-estudos-inteligente" />,
  // O Radar Simples (000184-000186): enquadramento e simulação do Simples
  // Nacional, da Pós em Rotinas do Simples. Nove módulos despachados pelo
  // primeiro segmento, e o de Abertura tem dois níveis abaixo dele —
  // `/abertura/<uuid>/<aba>` —, como o HUB DP e pela mesma razão: o contador
  // manda ao colega o link da planilha de honorários daquele cliente.
  //
  // O questionário que o CLIENTE do aluno responde NÃO mora aqui: ele é a rota
  // pública `/formulario/:token`, em `App.tsx`, porque quem o abre não tem
  // sessão no hub — a mesma forma de `/orcamento/:token`.
  "radar-simples": RadarSimples,
  // O SindiFácil (000212): a busca no cadastro nacional de entidades sindicais,
  // da Pós DP. UMA tela, sem sub-rota — o que se compartilha é a ferramenta, não
  // a consulta, e ninguém manda ao colega "a busca de metalúrgicos em SP". É o
  // oposto do Mapa de Concursos, onde a UF viaja na query justamente porque "os
  // abertos no Paraná" é o que se manda a alguém.
  "sindifacil": SindiFacil,
  // O CNAExpert (000216): FPAS, RAT e grau de risco por CNAE, da Pós DP. UMA
  // tela, sem sub-rota, pela mesma razão do SindiFácil — o que se compartilha é
  // a ferramenta, não a consulta.
  //
  // Ele lê o CATÁLOGO ÚNICO de CNAEs, o mesmo do Radar Simples e da busca de
  // ocupações do MEI. O porte NÃO trouxe a lista do Bubble: desde a 000189 há
  // uma lista só no hub, e o que veio de lá foram os enquadramentos
  // ALTERNATIVOS de FPAS, que o catálogo anunciava em 91 CNAEs sem ter onde
  // guardar.
  "cnaexpert": CnaExpert,
  // As Calculadoras da Reforma (000221), da Pós RT: três calculadoras ligadas à
  // API oficial da Receita Federal, despachadas pelo primeiro segmento
  // (`/regime-geral`, `/simplificado`, `/base-calculo`).
  //
  // Ela é a primeira ferramenta do hub cujo RESULTADO vem de fora: nenhuma conta
  // de tributo acontece aqui, e não deve passar a acontecer — a resposta é da
  // Receita, e uma conta nossa ao lado seria a segunda definição do mesmo
  // número. O caminho é a Edge Function `calcular-tributos`, porque o navegador
  // não alcança a API do gov: medido em 14/09, o preflight responde 403 e a
  // resposta simples não traz `access-control-allow-origin`.
  "calculadoras": Calculadoras,
  // O CST e ClassTrib (000223), da Pós RT: a tabela de situações e
  // classificações tributárias da Reforma. Um segmento só, `favoritos`, porque
  // é a única seção com endereço próprio; a classificação escolhida, o tributo
  // e a data viajam na QUERY — "me manda o 200019" é o que um contador manda ao
  // colega, e o botão de voltar tem de desfazer a escolha em vez de sair da
  // ferramenta.
  //
  // O catálogo NÃO mora no hub: ele é da Receita e muda com a norma. Uma cópia
  // aqui seria a segunda lista que envelhece em silêncio (a cicatriz das duas
  // listas de CNAE), e o app de origem provou isso — 130 classificações contra
  // as 164 que a Receita publica hoje.
  "cst-e-classtrib": CstClassTrib,
  // A Central Inteligente IBS-CBS (000230/000231), da Pós RT: o acervo das 5
  // normas da Reforma (2.070 dispositivos, com a leitura da casa sobre cada um)
  // e a IA que responde sobre ele.
  //
  // Ela despacha o próprio `*`, e um dos segmentos é `ia` — a tela dedicada da
  // conversa, que é a MESMA `IaDoCurso` das outras duas. Por isso o shell dela é
  // por ROTA e não por card: ver `pedeShell`.
  "central-ibs-cbs": CentralIbsCbs,
  // O CURSO que mora no hub (000375): o Fundamentos da Reforma Tributária, o low ticket que vinha da
  // Lovable. As aulas são vídeos do ScaleUp com o texto ao lado, e o painel, a aula e o certificado
  // são sub-rotas (`/aula/<módulo>/<aula>`, `/certificado`), pela razão do HUB DP. Uma tela com o
  // card como parâmetro, como a de conteúdo: o curso seguinte que morar aqui entra com uma linha.
  "fundamentos-rt": () => <CursoNoHub card="fundamentos-rt" />,
  // A Central do Perito (000385), da Formação em Perícia: a gestão das perícias judiciais do aluno, que era
  // um app próprio. As perícias, o cadastro (`/pericias/nova`) e a ficha (`/pericias/<uuid>`, com a aba na
  // query, `?aba=tarefas`), despachados pelo `*` como no Leitor de Processos.
  "central-do-perito": CentralDoPerito,
};

export default function Ferramenta() {
  const { slug = "", "*": resto = "" } = useParams();
  const [params] = useSearchParams();
  const hub = useHub();

  // O USO (000303, F10): a ferramenta que mora aqui abre por `<Link>`, sem ida
  // ao servidor, e até 25/09 isso não deixava rastro nenhum. Registra-se AQUI,
  // onde a abertura de fato acontece, e só quando ela acontece — o banco decide
  // se conta (e não conta a recarga da mesma página em 5 minutos). O hook vem
  // antes de qualquer `return`: um hook depois dele derruba a rota com o React
  // #310, e o hub não tem eslint para avisar.
  const curso = params.get("curso");
  const abriu =
    hub.fase === "pronto" &&
    [...hub.hub.cardsDeCurso, ...hub.hub.cardsDeConta].some(
      (c) => c.card_slug === slug && c.destino === "interno" && Boolean(FERRAMENTAS[slug]),
    );
  useEffect(() => {
    if (abriu) registrarAbertura(slug, curso);
  }, [abriu, slug, curso]);

  // A NOVIDADE desta ferramenta (000304), se a equipe divulgou uma. Também
  // antes de qualquer `return`, pela mesma razão do registro acima.
  const novidades = useNovidades();
  const novidade = abriu ? (novidades.get(slug) ?? null) : null;

  if (hub.fase === "carregando") return <Carregando />;

  if (hub.fase !== "pronto") {
    // 'erro' e 'sem-vinculo' já têm tela própria na inicial, e duplicar as duas
    // mensagens aqui as faria divergir na primeira vez que uma fosse reescrita.
    return <Redireciona />;
  }

  // Os dois escopos: o card pode ser de curso ou de conta, e uma ferramenta
  // interna futura pode ser qualquer um dos dois (Contrate um Contador é de conta).
  const card = [...hub.hub.cardsDeCurso, ...hub.hub.cardsDeConta].find(
    (c) => c.card_slug === slug,
  );
  const Tela = FERRAMENTAS[slug];

  if (!card || !Tela || card.destino !== "interno") {
    // Cada motivo tem texto próprio, e o do contrato manda ao lugar onde se
    // resolve. "Em breve" e "não é seu" viram telas diferentes pela mesma razão da
    // 000062: confundi-los manda ao suporte quem só precisava esperar, e faz
    // esperar quem só precisava assinar.
    const motivo = !card
      ? "Esta ferramenta não faz parte dos cursos que você tem hoje. Se você acabou de comprar, ela aparece assim que a compra for processada."
      : card.trancado
        ? "O curso desta ferramenta está aguardando a assinatura do contrato. Assine na página do curso para liberar."
        : card.destino === "em-breve"
          ? "Esta ferramenta ainda está sendo construída aqui dentro. Ela aparece na página do curso quando estiver pronta."
          : "Esta ferramenta abre por outro caminho. Volte à página do curso e clique no card dela.";

    return (
      <Aviso
        titulo="Ferramenta não disponível"
        acao={
          <Link to="/" className="pill-cta">
            Voltar para o início
          </Link>
        }
      >
        {motivo}
      </Aviso>
    );
  }

  return (
    <Moldura
      nome={hub.hub.nome}
      avatarUrl={hub.hub.avatarUrl}
      shell={pedeShell(slug, resto)}
      largura={LARGAS[slug]}
      // O botão de voltar ao curso, pedido por Victor em 26/08 para TODAS as
      // ferramentas. Fica aqui e não nas telas por dois motivos: só três o
      // tinham (e com o texto "Voltar à Escola" cravado, falso para quem abriu o
      // HUB DP pela Pós DP), e assim a próxima ferramenta interna nasce com ele.
      // Quem resolve para qual curso é `cursoDeVolta`, com o `?curso=` que
      // `aberturaDoCard` já põe no link.
      volta={cursoDeVolta(hub.hub, slug, params.get("curso"))}
      novidade={novidade}
      // O botão de AVALIAR (000326, F9), só em ferramenta: curso bônus e módulo
      // da FBC não recebem avaliação, e o banco recusaria do mesmo jeito.
      avaliar={card.card_tipo === "ferramenta" ? { card: slug, nome: card.card_nome } : null}
    >
      <Tela />
    </Moldura>
  );
}

/**
 * As ferramentas que são APLICATIVO, não página: altura exata do viewport,
 * scroll interno e sem Rodapé — chat com composer no pé não convive com rodapé
 * de página (o aluno rolaria para um beco atrás da caixa de pergunta). Lojas e
 * ajuda continuam alcançáveis pelo MenuConta do Topo. Opt-in por slug de
 * propósito: o padrão com Rodapé fica intacto para IR Proposta, MEI Pro e
 * FerramentaDeConteudo — sumir com o Rodapé delas pela porta dos fundos é a
 * cicatriz do "Rodape solto no flex" por outro caminho.
 */
const APP_SHELL = new Set(["contabiliza-ai", "peric-ia"]);

/**
 * E as ferramentas em que só ALGUMAS telas são aplicativo.
 *
 * A Central IBS-CBS (000230) tem as duas naturezas no mesmo card: o acervo é
 * PÁGINA — rola, tem barra de volta e rodapé como qualquer ferramenta — e a tela
 * dedicada da conversa é APLICATIVO, pela mesma razão das outras IAs (composer
 * no pé não convive com rodapé de página).
 *
 * Opt-in por rota, como o `APP_SHELL` é opt-in por slug: cravar o card inteiro
 * como shell tiraria o rodapé e a barra de volta de seis telas de leitura.
 */
const APP_SHELL_POR_ROTA: Record<string, (resto: string) => boolean> = {
  "central-ibs-cbs": (resto) => resto.replace(/^\/+/, "").split("/")[0] === "ia",
};

function pedeShell(slug: string, resto: string): boolean {
  if (APP_SHELL.has(slug)) return true;
  return APP_SHELL_POR_ROTA[slug]?.(resto) ?? false;
}

/**
 * As ferramentas que pedem mais largura que a coluna de leitura padrão.
 *
 * O padrão é `max-w-6xl`, e ele é a largura de LEITURA: uma coluna de texto e
 * cards. O ForoFácil não é isso — são duas colunas de conteúdo lado a lado (o
 * mapa que fica e a ficha do tribunal), a pedido de Victor em 03/09.
 *
 * O ajuste mora AQUI e não na tela porque a `BarraDeVolta` e o `Rodape` têm de
 * acompanhar: os dois são desenhados pela `Moldura`, e conteúdo mais largo que
 * eles faz a página parecer ter dois alinhamentos — a cicatriz do "Rodape solto
 * no flex", de 19/08, pelo caminho inverso. Opt-in por slug, como o `APP_SHELL`.
 */
const LARGAS: Record<string, string> = { "foro-facil": "max-w-[86rem]" };

/**
 * A moldura das ferramentas internas: topo, conteúdo e rodapé.
 *
 * O `<Rodape />` vai DENTRO de um container com `max-w-6xl px-4`, e não solto no
 * `flex` da página. Não é detalhe de estilo: `Rodape` não tem container próprio — ele
 * depende de quem o envolve, e é assim que `Curso.tsx` e `Perfil.tsx` o usam. Solto,
 * ele esticava de ponta a ponta da tela enquanto o conteúdo acima ficava centrado, e
 * a página parecia ter dois alinhamentos diferentes. Foi o que o Victor viu em 19/08.
 *
 * `max-w-6xl` porque é a largura que `IrProposta` usa no `<main>` dela; as duas têm
 * de bater, senão o rodapé fica mais largo ou mais estreito que o conteúdo.
 */
function Moldura({
  nome,
  avatarUrl,
  shell = false,
  volta,
  largura = "max-w-6xl",
  novidade = null,
  avaliar = null,
  children,
}: {
  nome: string | null;
  avatarUrl?: string | null;
  shell?: boolean;
  volta?: Volta | null;
  /** A classe de largura da BARRA e do RODAPÉ — ver `LARGAS`. */
  largura?: string;
  /**
   * A novidade divulgada (000304). NÃO entra no aplicativo (`shell`): numa tela
   * de altura exata do viewport, uma faixa a mais empurra o composer para fora —
   * a mesma razão de a barra de volta não entrar lá.
   */
  novidade?: Novidade | null;
  /**
   * A ferramenta que o aluno pode avaliar (000326). NÃO entra no aplicativo
   * (`shell`), pela razão da barra de volta: lá o botão mora na coluna das
   * conversas da IA (`IaDoCurso`).
   */
  avaliar?: { card: string; nome: string } | null;
  children: React.ReactNode;
}) {
  if (shell) {
    // `h-dvh` (não 100vh) por causa do teclado do iOS, e a cadeia de `min-h-0`
    // começa aqui: sem ela em CADA nível de flex/grid até o rolo de mensagens,
    // o composer sai do viewport.
    //
    // A barra de volta NÃO entra aqui: numa tela de altura exata do viewport,
    // qualquer linha a mais no topo empurra o composer para fora. A IA de curso
    // volta pelo Topo, como já fazia.
    //
    // E a altura desconta a barra do «ver como» (000335), que fica fixa no pé:
    // sem isto ela cobriria o composer. Fora do «ver como» a variável não existe
    // e a conta é a de sempre, 100dvh.
    return (
      <div className="flex h-[calc(100dvh-var(--cf-ver-como,0px))] flex-col bg-fundo">
        <Topo atual="inicio" nome={nome} avatarUrl={avatarUrl} />
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </div>
    );
  }
  return (
    <div className="flex min-h-dvh flex-col bg-fundo">
      <Topo atual="inicio" nome={nome} avatarUrl={avatarUrl} />
      <BarraDeVolta
        volta={volta ?? null}
        largura={largura}
        acao={avaliar ? <BotaoAvaliarFerramenta card={avaliar.card} nome={avaliar.nome} /> : null}
      />
      {novidade && <AvisoDeNovidade novidade={novidade} largura={largura} />}
      <div className="flex-1">{children}</div>
      <div className={`mx-auto w-full px-4 sm:px-6 ${largura}`}>
        <Rodape />
      </div>
    </div>
  );
}

/**
 * A faixa da NOVIDADE no alto da ferramenta (000304). Fechar é conveniência de
 * quem vê (`localStorage`, por atualização): a próxima novidade da mesma
 * ferramenta tem outro id e aparece de novo.
 */
function AvisoDeNovidade({ novidade, largura }: { novidade: Novidade; largura: string }) {
  const [fechada, setFechada] = useState(() => novidadeJaVista(novidade.atualizacao_id));
  if (fechada) return null;
  return (
    <div className={`mx-auto w-full px-4 pb-4 pt-4 sm:px-6 ${largura}`}>
      <div
        role="status"
        data-aviso-de-novidade={novidade.atualizacao_id}
        className="flex flex-wrap items-start gap-3 rounded-cf border border-aviso-borda bg-aviso-fundo px-4 py-3 text-sm text-aviso-tinta"
      >
        <p className="min-w-0 flex-1 leading-relaxed">
          <strong className="font-semibold">Novidade</strong>
          <span className="mx-1.5" aria-hidden="true">
            ·
          </span>
          {novidade.texto}
        </p>
        <button
          type="button"
          data-fechar-novidade
          onClick={() => {
            marcarNovidadeVista(novidade.atualizacao_id);
            setFechada(true);
          }}
          className="shrink-0 text-xs font-semibold underline-offset-2 hover:underline"
        >
          Entendi
        </button>
      </div>
    </div>
  );
}

/** Sem sessão útil, a inicial resolve — e ela já sabe o que dizer em cada caso. */
function Redireciona() {
  return (
    <div className="grid min-h-dvh place-items-center bg-fundo p-6 text-center">
      <div>
        <p className="text-cinza">Não conseguimos carregar a sua conta.</p>
        <Link to="/" className="pill-cta mt-4 inline-flex">
          Ir para o início
        </Link>
      </div>
    </div>
  );
}
